// ============================================================
// TMR Portal Admin — Tooltip: bolha flutuante na superfície inversa do sistema
// Substitui o `title` nativo (bolha preta do navegador). Abre no hover e no
// foco (acessível por teclado), posiciona-se acima do gatilho e cai para baixo
// quando não há espaço. Renderizada via portal para escapar de overflow/clipping.
// ============================================================
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface Placement {
  top: number;
  left: number;
  arrowLeft: number;
  arrowTop: number;
}

const GAP = 8;
const MARGIN = 8;
const ARROW = 8;

export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;
    const t = trigger.getBoundingClientRect();
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const centerX = t.left + t.width / 2;

    // Acima por padrão; cai para baixo se não couber.
    const above = t.top - h - GAP >= MARGIN;
    const top = above ? t.top - h - GAP : Math.min(vh - h - MARGIN, t.bottom + GAP);

    let left = centerX - w / 2;
    left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));

    const arrowLeft = Math.max(ARROW, Math.min(centerX - left - ARROW / 2, w - ARROW * 2));
    const arrowTop = above ? h - ARROW / 2 : -ARROW / 2;

    setPos({ top, left, arrowLeft, arrowTop });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  const show = () => setOpen(true);
  const hide = () => {
    setOpen(false);
    setPos(null);
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={0}
        style={{ display: 'inline-flex', outline: 'none' }}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            className="pa-tooltip"
            style={{
              top: pos ? pos.top : -9999,
              left: pos ? pos.left : -9999,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {content}
            {pos && <span className="pa-tooltip__arrow" style={{ left: pos.arrowLeft, top: pos.arrowTop }} />}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Texto com dica: sublinha o próprio texto com um tracejado na cor de destaque
 * do site e revela o tooltip no hover/foco. Substitui o ícone "i" — a affordance
 * é o próprio sublinhado, mais discreto e profissional.
 */
export function Hint({ tip, children }: { tip: ReactNode; children: ReactNode }) {
  return (
    <Tooltip content={tip}>
      <span
        style={{
          textDecorationLine: 'underline',
          textDecorationStyle: 'dashed',
          textDecorationColor: 'var(--forest-700)',
          textUnderlineOffset: 3,
          textDecorationThickness: 1,
          cursor: 'help',
        }}
      >
        {children}
      </span>
    </Tooltip>
  );
}
